import { useState } from "react";
import { Check, Apple, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHead } from "@/components/Reveal";
import { PLANS } from "@/data/site";

export const Pricing = ({ onJoin }) => {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="relative py-32 sm:py-40 bg-[color:var(--kmg-deep)]" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="04 — Pricing Plans for Farmers"
          title="One platform. Complete control. Anytime, anywhere."
          sub="Built for every type of farm. Simple. Powerful. Affordable."
        />

        <Reveal>
          <div className="inline-flex items-center gap-1 mt-12 border border-[rgba(244,241,230,0.2)] rounded-full p-1" data-testid="pricing-toggle">
            {[
              { k: false, l: "Monthly" },
              { k: true, l: "Annual" },
            ].map((o) => (
              <button
                key={o.l}
                onClick={() => setAnnual(o.k)}
                data-testid={`pricing-toggle-${o.l.toLowerCase()}`}
                className={`rounded-full px-6 py-2.5 font-mono-label text-[11px] uppercase tracking-[0.16em] ${
                  annual === o.k ? "bg-[color:var(--kmg-lime)] text-[color:var(--kmg-forest)]" : "text-[color:var(--kmg-bone-dim)]"
                }`}
                style={{ transition: "background-color .35s ease, color .35s ease" }}
              >
                {o.l}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {PLANS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <div
                data-testid={`plan-card-${p.id}`}
                className={`lift h-full flex flex-col p-8 border ${
                  p.featured
                    ? "border-[color:var(--kmg-lime)] bg-[color:var(--kmg-emerald)]"
                    : "border-[rgba(111,227,155,0.16)] bg-[color:var(--kmg-forest)] hover:border-[color:var(--kmg-lime)]"
                }`}
              >
                {p.featured && (
                  <span className="self-start mb-5 font-mono-label text-[9px] uppercase tracking-[0.2em] bg-[color:var(--kmg-lime)] text-[color:var(--kmg-forest)] px-3 py-1">
                    Most chosen
                  </span>
                )}
                <h3 className="font-display text-xl">{p.name}</h3>
                <p className="text-xs mt-2 text-[color:var(--kmg-bone-dim)]">{p.for}</p>

                <p className="font-display text-2xl lg:text-3xl mt-8 whitespace-nowrap" data-testid={`plan-price-${p.id}`}>
                  {annual ? p.yearly : p.monthly}
                  <span className="text-sm font-normal text-[color:var(--kmg-bone-dim)]">{annual ? "/year" : "/month"}</span>
                </p>
                {annual && <p className="font-mono-label text-[10px] uppercase tracking-[0.16em] mt-2 text-[color:var(--kmg-gold)]">{p.save}</p>}

                <p className="text-sm mt-6 text-[color:var(--kmg-bone-dim)] leading-relaxed">{p.blurb}</p>

                {p.inherits && <p className="font-mono-label text-[10px] uppercase tracking-[0.16em] mt-7 text-[color:var(--kmg-gold)]">{p.inherits}</p>}
                <ul className="mt-5 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-[13px] text-[color:var(--kmg-bone-dim)]">
                      <Check size={14} className="text-[color:var(--kmg-lime)] shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={onJoin}
                  data-testid={`plan-cta-${p.id}`}
                  className={`mt-9 rounded-full py-3.5 text-sm font-semibold ${
                    p.featured
                      ? "bg-[color:var(--kmg-lime)] text-[color:var(--kmg-forest)] hover:bg-[#8df0b3]"
                      : "border border-[color:var(--kmg-lime)] text-[color:var(--kmg-lime)] hover:bg-[color:var(--kmg-lime)] hover:text-[color:var(--kmg-forest)]"
                  }`}
                  style={{ transition: "background-color .4s ease, color .4s ease" }}
                >
                  {p.cta}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-20 flex flex-wrap items-center gap-4">
            <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[color:var(--kmg-gold)] w-full sm:w-auto sm:mr-4">
              Download the app
            </p>
            {[
              { icon: Apple, top: "Download on", name: "App Store", tid: "download-ios-btn" },
              { icon: Smartphone, top: "Get it on", name: "Google Play", tid: "download-android-btn" },
            ].map((s) => (
              <button
                key={s.name}
                data-testid={s.tid}
                onClick={() => toast.info(`${s.name} listing coming soon`, { description: "Register today and we'll send you the download link on release." })}
                className="inline-flex items-center gap-3 border border-[rgba(244,241,230,0.24)] px-7 py-3.5 hover:border-[color:var(--kmg-lime)]"
                style={{ transition: "border-color .4s ease" }}
              >
                <s.icon size={19} />
                <span className="text-left">
                  <span className="block font-mono-label text-[9px] uppercase tracking-[0.2em] text-[color:var(--kmg-bone-dim)]">{s.top}</span>
                  <span className="block text-sm">{s.name}</span>
                </span>
              </button>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
